import { useRef, useState, useEffect } from 'react';
import { ErrorMessage } from 'formik';
import CloudAddSvg from 'assets/svgs/CloudAddSvg.js';
import CaptureSvg from 'assets/svgs/CaptureSvg.js';
import Webcam from 'react-webcam';
import TextError from './TextError';

const CaptureImage = ({ label, name, formik }) => {
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [tagInput, setTagInput] = useState(""); 

  useEffect(() => {
    if (!formik.values[name]) {
      setUploadedImage(null);
      setShowCamera(false);
      setTagInput('')
    }
  }, [formik.values[name]]);

  // Convert base64
  const handleImageUpload = (base64) => {
    setUploadedImage(base64);
    formik.setFieldValue(name, base64);
    formik.setFieldTouched(name, true);
    setShowCamera(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => handleImageUpload(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleBrowseClick = () => fileInputRef.current?.click();

  const captureFromCamera = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    handleImageUpload(imageSrc);
  };

  const handleAddTag = () => {
    if (tagInput.trim() === "") return;
    const existingTags = formik.values[`${name}_tags`] || [];
    formik.setFieldValue(`${name}_tags`, [...existingTags, tagInput.trim()]);
    setTagInput("");
  };

  const handleRemoveTag = (tagToRemove) => {
    const existingTags = formik.values[`${name}_tags`] || [];
    formik.setFieldValue(
      `${name}_tags`,
      existingTags.filter((t) => t !== tagToRemove)
    );
  };

  const isError = formik.touched[name] && formik.errors[name];

  return (
    <div className="w-full h-auto border-2 border-dashed border-[#DCE4E8] rounded-xl flex flex-col items-center justify-center text-center p-6">
      <h2 className="text-base font-semibold mb-2">{label}</h2>

      {showCamera ? (
        <div className="flex flex-col items-center gap-3">
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            className="rounded-lg w-[250px] h-[200px] object-cover border border-gray-300"
          />
          <div className="flex gap-3">
            <button
              type="button"
              onClick={captureFromCamera}
              className="bg-primary text-white px-4 py-2 rounded-[10px] hover:opacity-90"
            >
              Capture
            </button>
            <button
              type="button"
              onClick={() => setShowCamera(false)}
              className="border border-gray-400 text-gray-600 px-4 py-2 rounded-[10px] hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          {uploadedImage ? (
            <img
              src={uploadedImage}
              alt="Preview"
              className="w-[150px] h-[150px] object-cover rounded-lg mb-4"
            />
          ) : (
            <CloudAddSvg />
          )}

          <input
            type="file"
            ref={fileInputRef}
            name={name}
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          <div className="flex flex-col items-center gap-2">
            <p className="text-sm text-gray3">JPEG, JPG, PNG up to 50MB</p>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleBrowseClick}
                className="rounded-[10px] text-primary text-opacity-40 border border-primary/50 px-3 py-2 hover:text-opacity-100 hover:border-primary"
              >
                {uploadedImage ? "Change Image" : "Browse File"}
              </button>

              <button
                type="button"
                onClick={() => setShowCamera(true)}
                className="rounded-[10px] text-light2 bg-primary border-primary/50 px-3 py-2 flex items-center gap-2 hover:opacity-90"
              >
                Take Image <CaptureSvg />
              </button>
            </div>

            {/* Optional Tag Input */}
            {uploadedImage && (
              <div className="mt-4 w-full">
                <label className="text-sm font-medium mb-1 block">Optional Tags</label>
                <div className="flex gap-2 flex-wrap">
                  {formik.values[`${name}_tags`] &&
                    formik.values[`${name}_tags`].map((tag, idx) => (
                      <span
                        key={idx}
                        className="bg-gray-200 px-2 py-1 rounded-full flex items-center gap-1"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="text-red-500 font-bold"
                        >
                          x
                        </button>
                      </span>
                    ))}
                </div>
                <div className="flex mt-2 gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add tag"
                className="border border-gray-300 outline-none rounded px-2 py-1 flex-1"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="bg-primary text-white px-4 py-1 rounded hover:opacity-90"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {isError && <TextError>{formik.errors[name]}</TextError>}
    </div>
  );
};

export default CaptureImage;
